// This file is part of Pollaris.
// Copyright 2026 Thomas Citharel
// SPDX-License-Identifier: AGPL-3.0-or-later

// Import the polls and votes stored in the localStorage of beta.framadate.org
// since the instance moved to framadate.org.
//
// beta.framadate.org serves public/legacy-storage.html, which sends its
// localStorage to the parent window with postMessage. This works because both
// origins are same-site, so the iframe has access to the same (non-partitioned)
// storage as when beta.framadate.org was visited directly.

import * as Storage from './storage.js';

const LEGACY_ORIGIN = 'https://beta.framadate.org';
const CURRENT_ORIGIN = 'https://framadate.org';
const BRIDGE_URL = `${LEGACY_ORIGIN}/legacy-storage.html`;
const MESSAGE_TYPE = 'pollaris:legacy-storage';
const IMPORTED_FLAG = 'legacy-storage-imported';

const URL_FIELDS = {
    polls: ['pollUrl', 'adminUrl'],
    votes: ['pollUrl', 'voteUrl'],
};

export function importLegacyStorage (timeout = 5000) {
    // The bridge page only sends its data to framadate.org, don't bother
    // elsewhere (e.g. in development).
    if (window.location.origin !== CURRENT_ORIGIN || isAlreadyImported()) {
        return Promise.resolve(false);
    }

    return new Promise((resolve) => {
        const iframe = document.createElement('iframe');
        iframe.hidden = true;
        iframe.setAttribute('aria-hidden', 'true');
        iframe.tabIndex = -1;
        iframe.src = BRIDGE_URL;

        let timer = null;

        const finish = (imported) => {
            window.removeEventListener('message', onMessage);
            clearTimeout(timer);
            iframe.remove();
            resolve(imported);
        };

        const onMessage = (event) => {
            // Only trust messages coming from our own iframe, from the legacy origin.
            if (event.origin !== LEGACY_ORIGIN || event.source !== iframe.contentWindow) {
                return;
            }

            const data = event.data;
            if (!isObject(data) || data.type !== MESSAGE_TYPE) {
                return;
            }

            try {
                for (const namespace of Object.keys(URL_FIELDS)) {
                    mergeNamespace(namespace, data[namespace]);
                }
                window.localStorage.setItem(IMPORTED_FLAG, new Date().toISOString());
            } catch (e) {
                finish(false);
                return;
            }

            finish(true);
        };

        // The flag is not set on timeout so the import is tried again on the
        // next visit.
        timer = setTimeout(() => finish(false), timeout);

        // Listen before inserting the iframe so the message cannot be missed.
        window.addEventListener('message', onMessage);
        document.body.appendChild(iframe);
    });
}

function mergeNamespace (namespace, entries) {
    if (!isObject(entries)) {
        return;
    }

    const storage = Storage.getNamespace(namespace);
    let changed = false;

    for (const [key, entry] of Object.entries(entries)) {
        // Never override an entry already stored on the current origin.
        if (Object.hasOwn(storage, key) || !isObject(entry)) {
            continue;
        }

        const rewrittenEntry = rewriteUrls(entry, URL_FIELDS[namespace]);
        if (rewrittenEntry) {
            storage[key] = rewrittenEntry;
            changed = true;
        }
    }

    if (changed) {
        Storage.saveNamespace(namespace, storage);
    }
}

// Rewrite the URLs of the entry so they point to framadate.org. Entries with
// URLs that don't belong to beta.framadate.org are rejected (this also
// protects against unsafe schemes such as `javascript:` since these URLs are
// then used in links).
function rewriteUrls (entry, fields) {
    const result = { ...entry };

    if (typeof result.pollUrl !== 'string') {
        return null;
    }

    for (const field of fields) {
        if (result[field] == null) {
            continue;
        }

        let url;
        try {
            url = new URL(result[field]);
        } catch (e) {
            return null;
        }

        if (url.origin !== LEGACY_ORIGIN) {
            return null;
        }

        result[field] = CURRENT_ORIGIN + url.pathname + url.search + url.hash;
    }

    return result;
}

function isAlreadyImported () {
    try {
        return window.localStorage.getItem(IMPORTED_FLAG) !== null;
    } catch (e) {
        // localStorage is not available, there is nothing we can do.
        return true;
    }
}

function isObject (variable) {
    return typeof variable === 'object' && !Array.isArray(variable) && variable !== null;
}
